import type { SimEvent } from "../protocol/events";
import type { EngineNode, EngineState } from "./state";

/**
 * Thermal regulation for one node (spec 0022, `01-simulation-model.md` §4's
 * "Thermal Regulators"). Runs inside step 2's node loop, immediately after
 * `processPhaseConversion` — no tick step is added.
 *
 * Deliberately NOT gated by `resolveSludgeGate`: `EngineNode.sludgeActiveThisTick`'s
 * own doc comment names exactly three passes it throttles — reaction, phase
 * conversion, push — and a regulator applying its configured ΔT is none of
 * them. A sludged regulator still resolves the *same* ΔT every tick; only its
 * `packetCount`, via the ordinary residence/sludge machinery in hazards.ts,
 * is affected.
 *
 * IS gated by `resolveInfestationGate` (spec 0033, owner decision 1): the
 * settled design names thermal regulation explicitly among the work vermin
 * halve, unlike sludge's narrower list above.
 */
export function processThermalRegulation(state: EngineState, node: EngineNode, events: SimEvent[]): void {
  if (node.isDestroyed || node.kind !== "thermalRegulator" || node.deltaTemp === null) return;
  // `03-hazards.md` §3.2: a scripted power surge freezes every thermalRegulator
  // (`facility.ts`'s frozenNodeIds already announces it), the same treatment
  // `heatShedOf` gives a cooling jacket. Bugfix (docs/GAPS.md G-G16): this read
  // was missing since this pass first shipped.
  if (state.powerFreezeTicks > 0) return;
  if (!node.verminActiveThisTick) return;
  if (node.buffer.length === 0) return;

  for (const packet of node.buffer) packet.temperature += node.deltaTemp;

  events.push({
    type: "node.regulated",
    nodeId: node.id,
    deltaTemp: node.deltaTemp,
    packetCount: node.buffer.length,
  });
}
